import { cloneElement, FC, ReactElement, useState } from 'react';
import { ButtonTheme, IconButton } from '../Button';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useFeatureFlagsContext } from '../../../FeatureFlags';
import { useVimeoVideoContext, VideoVimeoProvider } from '../../../Vimeo/VimeoVideoContext';
import { VimeoVideoRuntimePill } from '../VimeoVideoRuntimePill';
import { VimeoVideo } from '../Video';

type ElementEducationVideoTileProps = {
  label: string;
  body: string;
  thumbNail?: ReactElement;
  theme?: ButtonTheme;
  className?: string;
  videoId?: string;
};

export const ElementEducationVideoTile: FC<ElementEducationVideoTileProps> = ({ videoId, ...props }) => {
  return (
    <VideoVimeoProvider
      videoId={videoId}
      embedOptions={{
        autoplay: 1,
      }}
    >
      <ElementEducationVideoTileInner {...props} />
    </VideoVimeoProvider>
  );
};

const ElementEducationVideoTileInner: FC<Omit<ElementEducationVideoTileProps, 'videoId'>> = ({
  label,
  body,
  thumbNail,
  className,
  theme = 'primary',
}) => {
  let thumbNailClone;
  const { iframeEmbedUrl } = useVimeoVideoContext();
  const [showVideo, setShowVideo] = useState(false);
  const { showElementDetailViewVideoThumbnails } = useFeatureFlagsContext();
  if (thumbNail) {
    thumbNailClone = cloneElement(thumbNail, {
      className: clsx('w-40 object-cover grayscale h-full', thumbNail.props.className),
    });
  }

  if (!showElementDetailViewVideoThumbnails) {
    return null;
  }

  return (
    <div className={clsx(className, 'flex w-full justify-center items-center my-10 ')}>
      <figure className="flex rounded-2xl bg-[#242629] w-[440px] relative overflow-hidden dark:shadow-classic shadow-lightModeClassic">
        <div className="pr-[35px] pb-6 px-6  pt-[16px] sm:pt-[22px] relative w-72">
          <div className="ElementEducationVideoTile-label uppercase text-palette-600 text-sm leading-[14px] font-medium tracking-[0.04em] mb-1">
            {label}
          </div>
          {body && (
            <div className="text-white text-xl leading-6">
              {body.substring(0, body.length - 1)}
              <span className="text-palette-600">{body.charAt(body.length - 1)}</span>
            </div>
          )}
          <motion.div
            whileTap={{ scale: 1.1 }}
            whileHover={{ scale: 1.2 }}
            className="absolute flex rounded-3xl right-[-19px] top-[32%] bg-palette-600 border-[#242629] z-10 w-12 h-12 border-2 justify-center items-center p-1"
          >
            <IconButton
              className="ElementEducationVideoTile-IconButton bg-palette-600 text-palette-600 p-[6px] justify-center items-center !border-2 !border-[#242629]"
              theme={theme}
              icon={'playCircle'}
              iconClassName="opacity-60 mix-blend-multiply"
              diameter={48}
              onClick={() => {
                setShowVideo(true);
              }}
            />
          </motion.div>
        </div>
        {iframeEmbedUrl && (
          <VimeoVideo
            onClose={() => {
              setShowVideo(false);
            }}
            isOpen={showVideo}
            videoUrl={iframeEmbedUrl}
          />
        )}
        {thumbNailClone && <>{thumbNailClone}</>}
        {thumbNailClone && (
          <div className="absolute bottom-[4px] right-[7px]">
            <VimeoVideoRuntimePill />
          </div>
        )}
      </figure>
    </div>
  );
};
